/**
 * FEAT-087 / S-365 — the Desk queue's "kind of work" grouping.
 *
 * The redesign's central claim is that the home screen should be one
 * prioritised list of everything stopped and waiting on a person, grouped
 * by *the kind of work the person has to do* — not by pipeline phase. This
 * module is that derivation, kept free of React so it can be tested
 * directly.
 *
 * The five groups, in closest-to-finish order, and what puts a piece in
 * each (the group descriptions are the design's own wording):
 *
 *   decide  the pipeline is finished and waiting on a yes
 *   read    an agent wants a human eye before it goes on
 *   check   a quick look, not a read
 *   fix     something broke outside the writing
 *   start   captured, and waiting on you to send it down the pipeline
 *
 * **Read vs check is decided by which agent stopped it**, which is the one
 * part of this that isn't obvious from `ui_state` alone. Both are
 * `awaiting_review`; what differs is whether the thing to look at is prose
 * or structure. The split is taken from the design's own example rows —
 * it groups "Auditor · 2 claims unverified" and "Writer · came back from
 * you" under Read, and "Librarian · outline ready" and "Art Director · 6
 * images" under Check. Generalised: the three prose stages (Writer,
 * Auditor, Editor) want reading; the rest want a glance.
 */

import { MAIN_PIPELINE_PHASES } from "@shared/fiction-phases";

export const QUEUE_GROUPS = ["decide", "read", "check", "fix", "start"] as const;
export type QueueGroup = (typeof QUEUE_GROUPS)[number];

export interface QueueGroupMeta {
  /** Uppercase label on the group header. */
  label: string;
  /** The plain-language description beside it. */
  description: string;
}

export const QUEUE_GROUP_META: Record<QueueGroup, QueueGroupMeta> = {
  decide: {
    label: "Decide",
    description: "the pipeline is finished and waiting on a yes",
  },
  read: {
    label: "Read",
    description: "an agent wants a human eye before it goes on",
  },
  check: {
    label: "Check",
    description: "a quick look, not a read",
  },
  fix: {
    label: "Fix",
    description: "something broke outside the writing",
  },
  start: {
    label: "Start",
    description: "captured, and waiting on you to send it down the pipeline",
  },
};

/** The phases whose output is prose a person actually has to read. */
const PROSE_PHASES = new Set([
  "phase_3_draft_generated", // Writer
  "phase_4_fact_checked", // Auditor
  "phase_5_corrected", // Editor
]);

export interface QueuePiece {
  ui_state?: string | null;
  generation_phases?: string | null;
  run_phase?: string | null;
  /** S-380 — what the agent that stopped this piece said about why. */
  stop_reason?: string | null;
  stop_detail?: string | null;
}

/**
 * The group a piece belongs to, or null when it is not waiting on a person
 * at all — anything mid-pipeline, published, scheduled or discarded. The
 * queue only shows what someone has to act on; the count of what the
 * agents are still doing is stated separately in the subtitle.
 */
export function queueGroupFor(piece: QueuePiece): QueueGroup | null {
  // FEAT-089 — a queued idea is with the pipeline, not with the reader. The
  // queue is "what needs you", and offering Start on a piece already waiting
  // to run invites a second press that the run endpoint answers with a
  // conflict.
  if (piece.run_phase === "queued") return null;
  switch (piece.ui_state) {
    case "idea":
      return "start";
    case "awaiting_approval":
      return "decide";
    case "failed":
      return "fix";
    case "awaiting_review":
      return PROSE_PHASES.has(piece.generation_phases ?? "") ? "read" : "check";
    default:
      // ai_* (still running), published, scheduled, rejected,
      // revision_requested (back with the pipeline), fiction_*.
      return null;
  }
}

/** True while an agent is still working — used for the "nothing there
 * needs you" half of the subtitle. */
export function isPipelineRunning(piece: QueuePiece): boolean {
  return typeof piece.ui_state === "string" && piece.ui_state.startsWith("ai_");
}

/**
 * The agent that stopped the piece — the first half of a row's status pill
 * ("Publisher · gate held"). Derived from the last completed phase, except
 * for the two states where the phase is not the point: an idea has no
 * completed phase, and an approval gate is always the Publisher's.
 */
export function stoppedByAgent(piece: QueuePiece): string {
  if (piece.ui_state === "idea") return "Idea";
  if (piece.ui_state === "awaiting_approval") return "Publisher";
  const phase = MAIN_PIPELINE_PHASES.find((p) => p.value === piece.generation_phases);
  return phase?.label ?? "Pipeline";
}

/**
 * The second half of the pill, and the one-line reason beneath it.
 *
 * S-380 — the design's rows carry a real diagnosis: "Auditor · 2 claims
 * unverified" over "Couldn't source the 43% retention figure…". That is content
 * out of the agent's own report, and it now arrives on the piece as
 * `stop_reason` / `stop_detail`, written once when the agent finished.
 *
 * The generic lines below are the floor, not the ceiling. They stay for every
 * piece whose agent had nothing specific to say — an idea nobody has run, a
 * gate held with everything passing — and for the pieces that finished before
 * those columns existed. A row never has to invent a diagnosis to have one.
 */
export function queueReasonFor(piece: QueuePiece): { pill: string; line: string } {
  const group = queueGroupFor(piece);
  const reason = piece.stop_reason?.trim();
  const detail = piece.stop_detail?.trim();
  if (reason || detail) {
    const generic = genericReasonFor(group);
    return {
      // Either half can be missing without falling back to both halves being
      // generic — the specific one is still better than the line it replaces.
      pill: reason || generic.pill,
      line: detail || generic.line,
    };
  }
  return genericReasonFor(group);
}

function genericReasonFor(group: QueueGroup | null): { pill: string; line: string } {
  switch (group) {
    case "decide":
      return {
        pill: "gate held",
        line: "Everything passed. Publishing needs your approval.",
      };
    case "fix":
      return {
        pill: "run failed",
        line: "The run stopped on an error. Retry once you have looked at it.",
      };
    case "start":
      return {
        pill: "not started",
        line: "Captured, but nothing has run on it yet.",
      };
    case "read":
      return {
        pill: "needs a read",
        line: "Waiting on a human eye before it goes on.",
      };
    case "check":
      return {
        pill: "needs a check",
        line: "A quick look, then it carries on.",
      };
    default:
      return { pill: "", line: "" };
  }
}

export interface GroupedQueue {
  group: QueueGroup;
  meta: QueueGroupMeta;
  pieces: QueuePiece[];
}

/**
 * Bucket pieces into the five groups, in the design's closest-to-finish
 * order, dropping groups that are empty. Everything not waiting on a person
 * is left out entirely.
 */
export function groupQueue<T extends QueuePiece>(
  pieces: readonly T[]
): Array<{ group: QueueGroup; meta: QueueGroupMeta; pieces: T[] }> {
  const buckets = new Map<QueueGroup, T[]>();
  for (const piece of pieces) {
    const group = queueGroupFor(piece);
    if (!group) continue;
    const bucket = buckets.get(group);
    if (bucket) bucket.push(piece);
    else buckets.set(group, [piece]);
  }
  return QUEUE_GROUPS.filter((g) => buckets.has(g)).map((group) => ({
    group,
    meta: QUEUE_GROUP_META[group],
    pieces: buckets.get(group) as T[],
  }));
}

/** A piece is overdue when its publication date has passed and it is still
 * sitting in the queue. The design rations this mark deliberately — it is
 * the only loud thing on a row. */
export function isOverdue(
  piece: QueuePiece & { publication_date?: string | null },
  now: Date = new Date()
): boolean {
  if (!piece.publication_date) return false;
  if (!queueGroupFor(piece)) return false;
  const due = new Date(piece.publication_date);
  if (Number.isNaN(due.getTime())) return false;
  return due.getTime() < now.getTime();
}
